import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import BrandMark from './BrandMark';

const BRAND_COLORS = {
    primary: '#1a1a1a',
    secondary: '#7C3AED',
    accent: '#C9A961',
    accentDeep: '#7F6224',
    light: '#F5F5F0',
    lightGray: '#E8E8E3',
    graphite: '#4A4A46',
};

// One date for both documents: they are accepted together at sign-up, so
// versioning them separately only invites confusion about which is in force.
const LAST_UPDATED = '27 August 2026';

const COMPANY = {
    name: 'SKILLVEND SOLUTIONS PRIVATE LIMITED',
    address: [
        'Flat No 3011, Prestige Jade Pavillion,',
        'Gear Road, Doddakannelli,',
        'Bangalore, Karnataka, India, 560103',
    ],
    email: 'helpdesk@skillvend.com',
};

/*
 * Rule 3(2) of the IT (Intermediary Guidelines and Digital Media Ethics Code)
 * Rules 2021 requires an intermediary to publish the NAME of its Grievance
 * Officer as well as a contact address — an email alone does not satisfy it.
 * The renderer omits the name line if this is ever blanked out, rather than
 * printing a placeholder into a legal document.
 */
const GRIEVANCE_OFFICER = {
    name: 'Joe Manuel',
    email: 'joe@skillvend.com',
};

/* ------------------------------------------------------------------
   Blocks

   Prose lives in data rather than in JSX text, which is how FaqSection
   is built. It keeps each document readable as a document, lets the
   contents rail be generated from the same source as the sections, and
   sidesteps react/no-unescaped-entities on every apostrophe.

   A block is a string (paragraph) or one of:
     { h }     sub-heading
     { ul }    bulleted list
     { dl }    defined terms, as [term, definition] pairs
     { note }  set-apart callout, for something a reader must not miss
     { warn }  the same, in red, for a consequence
   ------------------------------------------------------------------ */

const TERMS_SECTIONS = [
    {
        id: 'terms-about',
        title: 'About these Terms',
        body: [
            'These Terms of Service (Terms) are a binding agreement between you and ' + COMPANY.name + ' (SkillVend, we, us, our), a company incorporated in India and having its registered office at the address given at the end of this page.',
            'They govern your use of the SkillVend mobile application, the website at skillvend.com, and every related service we provide (together, the Platform). You accept them when you tick the box at sign-up, and each time you continue to use the Platform.',
            'Please read them alongside our Privacy Policy, which forms part of these Terms and explains what we do with your personal data.',
            { note: 'If you do not agree with these Terms, do not use the Platform. If you have already registered, you can delete your account at any time — see Suspension, termination and deletion below.' },
        ],
    },
    {
        id: 'terms-definitions',
        title: 'Definitions',
        body: [
            {
                dl: [
                    ['SkillVender', 'An individual registered on the Platform to find and perform work. Referred to in the App as a Professional.'],
                    ['SkillEmployer', 'A business, establishment or organisation registered on the Platform to post work. Referred to in the App as an Employer.'],
                    ['Job', 'Short-term paid work offered for one or more specific days, at a published rate.'],
                    ['Internship', 'A training engagement of a fixed duration, undertaken for experience and a certificate, which may or may not carry a stipend.'],
                    ['Shift', 'A single dated slot within a Job or Internship, with its own vacancies and published rate.'],
                    ['Listing', 'A Job or Internship as published on the Platform, including its role, venue, dates, timings and rate.'],
                    ['Engagement', 'An accepted allocation of a SkillVender to a Shift, and the work arrangement that follows from it.'],
                    ['Codes', 'The numeric codes read out at a venue to open and close a Shift, described under Attendance below.'],
                ],
            },
        ],
    },
    {
        id: 'terms-eligibility',
        title: 'Eligibility and age',
        body: [
            'You may use the Platform only if you can enter into a legally binding contract under the Indian Contract Act 1872, and only if we have not previously suspended or removed you.',
            { h: 'Jobs — 18 and over' },
            'You must be at least 18 years old to apply for, accept or perform any Job on the Platform. This is a strict requirement and we do not waive it.',
            { h: 'Internships — 17 and over, for students' },
            'We allow an Internship to be undertaken from the age of 17, but only where you are enrolled at a college or other recognised institution and can produce a valid institutional identity document. A SkillVender under 18 may not apply for, accept or perform a Job.',
            'If you are under 18, you confirm that your parent or legal guardian has read these Terms and our Privacy Policy, consents to your use of the Platform and to your undertaking the Internship, and accepts these Terms on your behalf. We may ask for verifiable evidence of that consent at any time, and may withhold or withdraw access until it is provided.',
            { h: 'Generally' },
            {
                ul: [
                    'One person may hold one SkillVender account only. Your mobile number identifies your account and cannot be changed once the account exists.',
                    'You must not use the Platform on behalf of anyone else, or allow anyone else to use your account.',
                    'A SkillEmployer account must be opened and operated by someone authorised to bind the business it represents.',
                ],
            },
            { note: 'If we find that an account has been opened below the applicable minimum age, or that age or student status has been misrepresented, we will suspend the account and delete the associated personal data, subject to any record we are required by law to keep.' },
        ],
    },
    {
        id: 'terms-account',
        title: 'Your account and verification',
        body: [
            'Registration is by mobile number and one-time password. We do not issue passwords, and we will never ask you for one.',
            'You agree to give us information that is accurate, current and complete, and to keep it that way. This includes your identity documents, your date of birth, your education and, where you are a SkillEmployer, your business registration details.',
            'Before your profile is made available to the other side of the marketplace, we carry out checks on the documents you provide.',
            { warn: 'Verification means that we have checked the documents submitted to us. It is not a guarantee, an endorsement, a background check, or a warranty of any person’s honesty, skill, character or fitness for a role. Both sides must still exercise their own judgement.' },
            'You are responsible for everything done through your account and for keeping your registered mobile number and device secure. Tell us immediately through Help and Support if you believe your account has been used without your authority.',
        ],
    },
    {
        id: 'terms-role',
        title: 'What SkillVend is, and what it is not',
        body: [
            'SkillVend operates a marketplace. We publish Listings, help the two sides find each other, verify identities, and keep the record of what was worked. We are an intermediary within the meaning of the Information Technology Act 2000.',
            {
                ul: [
                    'SkillVend is not the employer of any SkillVender, and nothing on the Platform creates a relationship of employment, agency, partnership or joint venture between you and us.',
                    'The work arrangement is between the SkillEmployer and the SkillVender. SkillVend is not a party to it.',
                    'We do not supervise, direct or control the performance of any work, and we do not control the premises at which it is performed.',
                    'We do not guarantee that any Listing will be filled, that any SkillVender will be allocated work, or that any particular volume of work or income will be available.',
                ],
            },
            'Where we assist with a dispute, issue a certificate on a SkillEmployer’s behalf, or help resolve a question about pay, we do so as an intermediary providing a service — not as a party to the engagement, and without assuming the obligations of either side.',
        ],
    },
    {
        id: 'terms-how',
        title: 'How work is arranged',
        body: [
            'Every Listing states the role, the venue, the dates, the timings and the rate before you apply. You should read it in full: applying is a commitment, not an enquiry.',
            {
                ul: [
                    'Applying to a Shift expresses your willingness to work it. Allocation is confirmed in the App.',
                    'For an Internship, applying is a commitment to the whole published duration, not to individual days.',
                    'A Shift may be withdrawn before allocation — for example where its vacancies are filled, or the SkillEmployer cancels it.',
                    'Once allocated, you are expected to attend. If you cannot, mark leave in the App as early as you can so the slot can be re-filled.',
                    'Repeated failure to attend an allocation you accepted may lead to suspension, and will be visible on your record.',
                ],
            },
        ],
    },
    {
        id: 'terms-attendance',
        title: 'Attendance and the codes',
        body: [
            'Attendance is what pay and certificates are calculated from, so the Platform records it in a way neither side can set on its own.',
            {
                ul: [
                    'On arriving at the venue you mark yourself as reached in the App. The App records the time and your device’s location at that moment.',
                    'The SkillEmployer reads out a code that opens the Shift, and a second code that closes it. Each is entered in the App.',
                    'Hours are logged only when both sides have acted. Neither side can record a day the other has not confirmed.',
                ],
            },
            'You agree not to share, pre-disclose or fabricate a code, and not to mark yourself as reached when you are not at the venue. Doing so is a serious breach of these Terms and grounds for immediate removal from the Platform, in addition to any other remedy available to us or to the affected SkillEmployer.',
            { note: 'Because location is recorded at the moment you mark yourself as reached, please read the Location section of our Privacy Policy before you accept an Engagement.' },
        ],
    },
    {
        id: 'terms-skillvenders',
        title: 'If you are a SkillVender',
        body: [
            'You agree that you will:',
            {
                ul: [
                    'Attend the Shifts you accept, on time, and for their full published duration.',
                    'Present yourself appropriately for the venue and follow the SkillEmployer’s reasonable, lawful instructions and site rules while you are there.',
                    'Comply with the safety rules in force at the venue, and report any unsafe condition or injury immediately, to the SkillEmployer and to us.',
                    'Treat the SkillEmployer’s staff, customers, property and confidential information with care and respect.',
                    'Not accept payment, tips or work outside the Platform in order to avoid the record that the Platform keeps.',
                    'Not misrepresent your identity, age, student status, qualifications, certifications or experience.',
                ],
            },
            'You are responsible for your own tax affairs in respect of what you earn, and for telling us if you are not lawfully entitled to work.',
        ],
    },
    {
        id: 'terms-employers',
        title: 'If you are a SkillEmployer',
        body: [
            'You agree that you will:',
            {
                ul: [
                    'Publish Listings that are accurate and genuine, and honour the role, timings and rate as published.',
                    'Provide a safe and lawful place of work, and comply with all labour, workplace safety, minimum wage, working hours and social security legislation that applies to you.',
                    'Comply with the Sexual Harassment of Women at Workplace (Prevention, Prohibition and Redressal) Act 2013, including maintaining an Internal Committee where required.',
                    'Not engage any SkillVender under 18 on a Job, and not assign hazardous work, night work, or any other work restricted by law, to a SkillVender under 18 on an Internship.',
                    'Not discriminate against any SkillVender on the basis of caste, religion, sex, gender, disability, region, language or any other protected characteristic.',
                    'Read out the opening and closing codes honestly, and not withhold a code from a SkillVender who has worked.',
                    'Pay in full and on time for the days worked, at the published rate, and make only those deductions the law requires.',
                    'Use the personal data of SkillVenders only to arrange and manage the Engagement, and for no other purpose.',
                ],
            },
            'Charges payable by SkillEmployers for use of the Platform are as notified to you and as displayed before you confirm a Listing.',
        ],
    },
    {
        id: 'terms-pay',
        title: 'Pay, charges and payouts',
        body: [
            'The rate for a Shift is published in its Listing, and that published rate is what the work is paid at. Pay is calculated from the attendance record described above.',
            {
                ul: [
                    'SkillVenders pay nothing to register, to apply, or to be allocated work. We take no commission out of what a SkillVender earns.',
                    'SkillVend charges SkillEmployers for use of the Platform. Those charges are shown to the SkillEmployer before a Listing is confirmed.',
                    'Statutory deductions that a payer is required by law to make — such as tax deducted at source — may reduce the amount you receive. These are not SkillVend charges.',
                    'You may be asked to provide bank account details and a Permanent Account Number before a payout can be released, so that payment and the associated tax records can be completed correctly.',
                ],
            },
            'The mechanics of a payout — who disburses it, by what method and on what timetable — are as described in the App for the Engagement concerned at the time you accept it. Where SkillVend does not itself disburse a payment, we are not the payer, and our role is limited to maintaining the attendance record and assisting with a dispute.',
            'If you believe an amount is wrong, raise it through Help and Support before the payout is released where possible, and in any event within 30 days. We will investigate and assist in good faith, using the record the Platform holds.',
        ],
    },
    {
        id: 'terms-certificates',
        title: 'Certificates',
        body: [
            'On completion of an Internship, SkillVend issues a certificate on behalf of the SkillEmployer you worked with. It reaches you through the App and at the email address on your profile.',
            'A certificate records what actually happened — the period worked, attendance, and the SkillEmployer’s assessment. It is issued only where you complete the period you committed to and your attendance record supports it. Allow up to two weeks after your last day.',
            'Because a certificate carries your name and the SkillEmployer’s name, the details on your profile must be accurate before you begin.',
        ],
    },
    {
        id: 'terms-ratings',
        title: 'Ratings and reviews',
        body: [
            'After an Engagement, each side may rate the other and leave a short comment. Ratings are visible on profiles and affect what work and what people are offered.',
            'You agree that any rating or comment you leave will be genuine, based on your own direct experience, and free of abusive, defamatory or discriminatory content. We may remove a rating that breaches these Terms, or that we reasonably believe to be retaliatory, traded, or otherwise not genuine.',
        ],
    },
    {
        id: 'terms-conduct',
        title: 'Acceptable use and objectionable content',
        body: [
            'SkillVend operates a zero-tolerance policy towards objectionable content and abusive behaviour. You must not use the Platform to:',
            {
                ul: [
                    'Post a fake, misleading or fraudulent Listing, or one for work that is unlawful.',
                    'Harass, abuse, bully, stalk, threaten, intimidate or defame any person.',
                    'Post or transmit content that is obscene, pornographic, paedophilic, hateful, racially or ethnically objectionable, or that incites violence.',
                    'Discriminate against any person on the basis of caste, religion, sex, gender, disability, region or language.',
                    'Solicit personal contact, favours or relationships in exchange for work or a favourable rating.',
                    'Impersonate any person, or misrepresent your affiliation with any person or business.',
                    'Infringe anyone’s intellectual property, or upload a document that is not yours to upload.',
                    'Interfere with the Platform, attempt to gain unauthorised access to it, scrape it, or introduce malicious code.',
                    'Circumvent the attendance mechanism, or use the Platform to arrange work in a way designed to escape its record.',
                ],
            },
            { warn: 'You can report objectionable content or an abusive user from the App at any time, through the report control on a profile or Listing, or through Help and Support. We review every report within 24 hours and act on it — including removing content and ejecting the offending user. Serious matters are referred to law enforcement.' },
        ],
    },
    {
        id: 'terms-content',
        title: 'Your content',
        body: [
            'You keep ownership of everything you upload — your documents, your photograph, your resume, your skill summary and your reviews.',
            'You grant SkillVend a non-exclusive, royalty-free licence to host, store, reproduce and display that content for the purpose of operating the Platform: showing your profile to the other side of a marketplace transaction, verifying you, producing certificates, and keeping the record of an Engagement. The licence lasts as long as we hold the content, and ends when it is deleted.',
            'You confirm that you have the right to grant that licence, and that your content does not infringe anyone else’s rights.',
        ],
    },
    {
        id: 'terms-ip',
        title: 'Our intellectual property',
        body: [
            'The Platform, its software, design and text, and the SkillVend name and marks, belong to us or to our licensors. Nothing in these Terms transfers any of it to you.',
            'You may use the Platform for its intended purpose. You may not copy, modify, reverse engineer, resell or create derivative works from it, or use our name or marks without our written permission.',
        ],
    },
    {
        id: 'terms-termination',
        title: 'Suspension, termination and deletion',
        body: [
            { h: 'By you' },
            'You may stop using the Platform at any time, and delete your account from Account then My Profile in the App, or at skillvend.com/deletemyProfile.',
            { h: 'By us' },
            'We may suspend or terminate your access — with notice where it is reasonable to give it, and without notice where it is not — if we reasonably believe that you have breached these Terms, that your continued access presents a risk to another user or to us, or that we are required by law to act.',
            { h: 'Either way' },
            'Termination does not release either of us from an obligation already incurred, in particular payment for days already worked. Sections that by their nature should survive termination will do so, including those on the content licence, disclaimers, liability, indemnity and governing law.',
        ],
    },
    {
        id: 'terms-disclaimer',
        title: 'Disclaimers',
        body: [
            'The Platform is provided on an as-is and as-available basis. To the fullest extent permitted by law, we exclude all warranties, express or implied, including any implied warranty of merchantability, fitness for a particular purpose and non-infringement.',
            'We do not warrant that the Platform will be uninterrupted, timely, secure or error-free, or that the information on it is complete and accurate at all times.',
            'We do not warrant the conduct, honesty, safety, solvency or suitability of any SkillVender or SkillEmployer, or the accuracy of anything they tell you. Verification is a documentary check, not a guarantee.',
            'Nothing in these Terms excludes or limits liability that cannot lawfully be excluded or limited, including liability for death or personal injury caused by our negligence, or for fraud.',
        ],
    },
    {
        id: 'terms-liability',
        title: 'Limitation of liability',
        body: [
            'To the fullest extent permitted by law, SkillVend and its directors, officers, employees and affiliates will not be liable for any indirect, incidental, special, consequential, exemplary or punitive damages, or for any loss of profit, revenue, business, goodwill, opportunity or data, arising out of or in connection with:',
            {
                ul: [
                    'your use of, or inability to use, the Platform;',
                    'the conduct, content, act or omission of any other user, whether online or at a venue;',
                    'any Engagement, including non-payment by a SkillEmployer or non-performance by a SkillVender;',
                    'any unauthorised access to or alteration of your content;',
                    'any other matter relating to the Platform.',
                ],
            },
            'Subject to the paragraph above on liability that cannot lawfully be excluded, our total aggregate liability to you for all claims arising in any twelve-month period will not exceed the greater of the total charges you paid to SkillVend in that period, or five thousand rupees.',
        ],
    },
    {
        id: 'terms-indemnity',
        title: 'Indemnity',
        body: [
            'You agree to indemnify and hold harmless SkillVend and its directors, officers, employees and affiliates against any claim, demand, loss, liability, cost or expense (including reasonable legal fees) arising out of your breach of these Terms, your violation of any law, your infringement of a third party’s rights, or your conduct in connection with an Engagement.',
        ],
    },
    {
        id: 'terms-changes',
        title: 'Changes to these Terms and to the Platform',
        body: [
            'SkillVend reserves the right to amend, update, add to, replace or withdraw these Terms, our Privacy Policy and any other policy governing the Platform, from time to time and at its discretion — for example to reflect a change in how the Platform works, the introduction or withdrawal of a feature, a change in our business, or a change in applicable law, regulation or regulatory guidance.',
            'Because that right affects you, we exercise it on the following basis:',
            {
                ul: [
                    'A revised version takes effect from the Last updated date shown at the top of the document, and replaces every earlier version from that date.',
                    'Where a change materially affects your rights or obligations — for example a change to charges, to how pay is calculated, to how we use your personal data, or to how disputes are resolved — we will give you reasonable prior notice before it takes effect. We will give that notice in the App, and where appropriate at the mobile number or email address on your profile. Except where the change is required by law or is needed to address a security risk, that notice will be at least 14 days.',
                    'Changes are not applied retrospectively. An Engagement you have already accepted continues to be governed by the Terms in force when you accepted it.',
                    'If you continue to use the Platform once a revised version has taken effect, you accept it. If you do not accept it, you should stop using the Platform, and you may delete your account.',
                    'Where the law requires your consent to a change — in particular to a new purpose for processing your personal data — we will ask for that consent rather than rely on your continued use.',
                    'We keep previous versions of these Terms and of the Privacy Policy, and will provide the version that applied on a given date on request through Help and Support.',
                ],
            },
            'We may also change, suspend or discontinue any part of the Platform itself, including any feature, at any time. Where we withdraw something you rely on, we will give reasonable notice unless the circumstances do not permit it.',
        ],
    },
    {
        id: 'terms-grievance',
        title: 'Grievance redressal',
        body: [
            'If you have a complaint about the Platform, about content on it, or about how these Terms have been applied to you, raise it through Help and Support in the App, or write to our Grievance Officer using the details at the end of this page.',
            'We acknowledge a complaint within 24 hours and aim to resolve it within 15 days of receipt, as required under the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules 2021. Reports of objectionable content or abusive behaviour are acted on within 24 hours.',
        ],
    },
    {
        id: 'terms-law',
        title: 'Governing law and disputes',
        body: [
            'These Terms are governed by the laws of India.',
            'If a dispute arises, please contact us first through Help and Support so that we can try to resolve it directly — most matters are settled this way. If it cannot be resolved, the courts at Bangalore, Karnataka will have exclusive jurisdiction, subject to any right you have under consumer protection law to bring proceedings where you reside.',
            'If any provision of these Terms is held to be invalid or unenforceable, the rest continues in force. Our failure to enforce a provision is not a waiver of it.',
        ],
    },
];

const PRIVACY_SECTIONS = [
    {
        id: 'privacy-about',
        title: 'About this Policy',
        body: [
            COMPANY.name + ' is the data fiduciary for the personal data described here. This Policy explains what we collect when you use the SkillVend app or website, why we collect it, who we share it with, and what you can ask us to do about it.',
            'It is written to meet the Digital Personal Data Protection Act 2023 and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules 2011. It forms part of our Terms of Service.',
            { note: 'In short: we collect what is needed to verify who you are, match you to work near you, prove what you worked, and pay or certify you for it. We do not sell your data, we do not run advertising, and we do not track you across other apps or websites.' },
        ],
    },
    {
        id: 'privacy-collect',
        title: 'What we collect',
        body: [
            { h: 'Identity and contact details' },
            {
                ul: [
                    'Your name and mobile number. The mobile number is how your account is identified and cannot be changed once the account exists.',
                    'Your email address, if you give one. It is optional, and we use it for certificates, confirmations and updates — not to sign you in.',
                    'Your date of birth and the age derived from it, your sex, and your profile photograph.',
                    'Your residential address, including city, state and PIN code.',
                    'The mobile number linked to your Aadhaar, where you provide it, so that a verification can be matched to you.',
                ],
            },
            { h: 'Verification documents' },
            'A government-issued identity document, and — where you are a student — an institutional identity document. SkillEmployers provide business registration details instead, which may include a GST number and a Corporate Identity Number.',
            'These are treated as sensitive personal data. They are used to verify you and to prevent fraud and impersonation, and for nothing else.',
            { h: 'Education and professional details' },
            {
                ul: [
                    'Your college or institution, your course, when you joined it, and your highest qualification.',
                    'Your skills, a short summary in your own words, the languages you speak, and whether you have worked before.',
                    'Your resume, and up to five certifications, where you choose to upload them.',
                ],
            },
            { h: 'For SkillEmployers' },
            'Your business name and address, your registration identifiers, your total and female headcount where you provide them, and the name, phone number and designation of each point of contact you nominate.',
            { h: 'Location' },
            {
                ul: [
                    'Your approximate location, so that work can be sorted by distance from where you stay. We store a coarse geographic index alongside it for that purpose.',
                    'Your device location at the moment you mark yourself as having reached a venue, recorded together with the time. This is what allows an attendance record to mean something to both sides.',
                ],
            },
            { warn: 'We do not track your location in the background, and we do not build a movement history. The app requests location only while it is in use, and a precise position is stored only at the moment you mark yourself as reached.' },
            { h: 'Records of your work' },
            'Your applications and allocations, the codes used to open and close each Shift, the times a Shift was started and completed, any leave you marked and the reason given, and the resulting status of each Engagement. A snapshot of your profile — including your phone number — is attached to an Engagement so that both sides have a stable record of what was agreed.',
            { h: 'Ratings and feedback' },
            'The rating, short comment and tags left about you after an Engagement, and those you leave about others.',
            { h: 'Support requests' },
            'What you write to us through Help and Support, together with your name and phone number at the time, so that we can respond.',
            { h: 'Technical data' },
            'A push notification token for your device and whether you have notifications enabled, so that we can tell you about an allocation or a change to it. We also process basic technical information needed to operate and secure the service, such as app version and error diagnostics.',
        ],
    },
    {
        id: 'privacy-not-collect',
        title: 'What we do not collect',
        body: [
            'It is as important to say what the app does not do:',
            {
                ul: [
                    'We do not access your camera or microphone. Neither permission is requested.',
                    'We do not read your contacts, your messages, your call logs or your files beyond the specific document or photograph you choose to upload.',
                    'We do not use advertising identifiers, and we do not carry any advertising or third-party analytics software. The app declares no tracking in its iOS privacy manifest.',
                    'We do not track you across other companies’ apps or websites, and we do not build advertising profiles.',
                    'We do not sell, rent or trade your personal data. We never have.',
                ],
            },
        ],
    },
    {
        id: 'privacy-use',
        title: 'Why we use it',
        body: [
            'We use your personal data to:',
            {
                ul: [
                    'create your account, verify who you are, and keep impostors and fraudulent listings off the Platform;',
                    'show you work near where you stay, and show SkillEmployers the people who match a Listing;',
                    'arrange, run and record an Engagement, including attendance through the codes;',
                    'calculate what is owed for the days worked, and produce the records that a payout and its tax reporting require;',
                    'issue certificates on behalf of a SkillEmployer;',
                    'send you operational messages about allocations, changes, leave and payouts;',
                    'respond to your support requests and to grievances;',
                    'keep the Platform safe — investigating misuse, abuse and breaches of our Terms;',
                    'meet our legal, tax and regulatory obligations.',
                ],
            },
            'We rely on your consent, given when you register and when you grant a device permission, and on the legitimate uses permitted under the Digital Personal Data Protection Act 2023 — including compliance with law. You can withdraw consent at any time, as described below.',
            'We do not make decisions producing legal effects about you by automated means alone.',
        ],
    },
    {
        id: 'privacy-sharing',
        title: 'Who we share it with',
        body: [
            { h: 'With the other side of an Engagement' },
            {
                ul: [
                    'A SkillEmployer considering or allocated to you sees your profile — your name, photograph, age, skills, education, languages, rating and, once you are allocated, your phone number so that they can reach you on the day.',
                    'A SkillEmployer does not receive your identity documents, your resume file, your address, or your Aadhaar-linked number.',
                    'You see the SkillEmployer’s business name, venue and location, rating, and the point of contact for the Shift.',
                ],
            },
            { h: 'With service providers who process data for us' },
            {
                dl: [
                    ['Cloud hosting and infrastructure', 'Authentication, database, file storage, server functions, push notifications and abuse prevention. This is where your account and your documents are held.'],
                    ['Mapping and location services', 'Displaying venues on a map and calculating distance from where you stay.'],
                    ['App build and delivery', 'Building the mobile app, distributing it through the app stores, and delivering updates to it.'],
                ],
            },
            'These providers act on our instructions under contract and may not use your data for their own purposes.',
            { h: 'Otherwise' },
            {
                ul: [
                    'Where we are required to by law, by a court, or by a lawful request from a government or law-enforcement agency.',
                    'Where necessary to establish, exercise or defend a legal claim, or to investigate a serious breach of our Terms.',
                    'To a buyer or successor if our business is reorganised, merged or acquired — in which case we will tell you, and this Policy will continue to apply until replaced.',
                ],
            },
        ],
    },
    {
        id: 'privacy-storage',
        title: 'Where it is stored, and for how long',
        body: [
            'Your data is held on managed cloud infrastructure operated by our hosting provider. Some of it may be stored or processed outside India by the service providers described above. Where that happens we rely on the contractual protections those providers are bound by, and on transfers permitted under Indian law.',
            'We keep personal data only as long as we need it:',
            {
                ul: [
                    'Profile and verification data — while your account is active.',
                    'Records of an Engagement, including attendance and payment records — for as long as required by tax, labour and company law, which in India is generally up to eight years.',
                    'Certificates — retained so that a certificate can be re-issued if you lose it.',
                    'Support and grievance records — up to three years, so that we can show how a complaint was handled.',
                    'Identity documents — deleted when your account is deleted, or sooner where verification no longer requires them.',
                ],
            },
        ],
    },
    {
        id: 'privacy-rights',
        title: 'Your rights',
        body: [
            'Under the Digital Personal Data Protection Act 2023 you may:',
            {
                ul: [
                    'ask what personal data we hold about you and how we have used and shared it;',
                    'ask us to correct or complete anything inaccurate — though your mobile number is fixed once the account exists, for the reasons given in our Terms;',
                    'ask us to erase your data, subject to records we must keep by law;',
                    'withdraw a consent you gave, including by turning off a device permission, recognising that some parts of the Platform will not work without it;',
                    'nominate someone to exercise these rights on your behalf if you die or become incapable of exercising them;',
                    'complain to us, and then to the Data Protection Board of India if you are not satisfied with our response.',
                ],
            },
            'To exercise any of these, write to us through Help and Support or at the address at the end of this page. We respond within 30 days, and will ask you to verify your identity first so that we do not disclose your data to someone else.',
        ],
    },
    {
        id: 'privacy-deletion',
        title: 'Deleting your account',
        body: [
            'You can delete your account from Account then My Profile in the App, or at skillvend.com/deletemyProfile.',
            'When you do, your profile stops being visible on the Platform, your ability to sign in is revoked, and your personal data — including your identity documents, resume and photograph — is erased from our active systems.',
            'We keep only what the law requires us to keep: the financial and attendance records behind engagements you have already worked, and anything needed for a live legal claim or investigation. Those are retained for the periods set out above and are not used for any other purpose. Backups are overwritten on their ordinary cycle.',
            { note: 'Deleting your account does not cancel an Engagement you have already accepted, and does not remove a SkillEmployer’s own records of work you did for them. Settle anything outstanding before you delete.' },
        ],
    },
    {
        id: 'privacy-security',
        title: 'How we protect it',
        body: [
            {
                ul: [
                    'Access to the Platform is by one-time password to your registered mobile number. There are no passwords to leak.',
                    'Data is encrypted in transit, and at rest by our infrastructure provider.',
                    'Documents are held in access-controlled storage, readable only by you and by authorised staff carrying out verification.',
                    'Database access is governed by server-side security rules, and requests are checked for app authenticity before they are served.',
                    'Staff access to personal data is limited to those who need it, and is logged.',
                ],
            },
            'No system is perfectly secure. If a breach occurs that is likely to affect you, we will notify you and the Data Protection Board of India as the law requires.',
        ],
    },
    {
        id: 'privacy-children',
        title: 'Users under 18',
        body: [
            'The Platform is for adults. Jobs are restricted to SkillVenders aged 18 and over.',
            'We permit a SkillVender aged 17 to undertake an Internship where they are enrolled at a college or other recognised institution. Where a SkillVender is under 18:',
            {
                ul: [
                    'we require the consent of a parent or legal guardian, and may ask for verifiable evidence of it;',
                    'we do not use their personal data for tracking, behavioural monitoring or targeted advertising — which we do not do for any user;',
                    'we collect only what the Internship and its certificate require.',
                ],
            },
            'We do not knowingly collect personal data from anyone under 17. If you believe a child has given us personal data, contact us and we will delete it.',
        ],
    },
    {
        id: 'privacy-cookies',
        title: 'Cookies and the website',
        body: [
            'skillvend.com uses only what is necessary to serve the site and keep your session working. We do not use advertising or cross-site tracking cookies, and we do not embed third-party analytics.',
            'Your browser lets you block or delete cookies; the informational parts of the site will continue to work if you do.',
        ],
    },
    {
        id: 'privacy-changes',
        title: 'Changes to this Policy',
        body: [
            'SkillVend reserves the right to revise, update or replace this Privacy Policy from time to time — to reflect a change in the Platform, in the service providers we rely on, or in applicable data protection law.',
            {
                ul: [
                    'The revised Policy takes effect from the Last updated date shown at the top of it.',
                    'Where a change is material — a new category of data, a new purpose, a new recipient, or a shorter or longer retention period — we will give you notice in the App and, where appropriate, at the mobile number or email address on your profile, before it takes effect.',
                    'Where the law requires your consent for a new purpose, we will ask for it. We will not quietly repurpose data you gave us for something else.',
                    'We keep previous versions and will provide the one that applied on a given date on request.',
                ],
            },
            'If you do not accept a revised Policy, you may withdraw your consent and delete your account as described above.',
        ],
    },
];

const DOCUMENTS = [
    {
        id: 'terms',
        title: 'Terms of Service',
        standfirst: 'The agreement between you and SkillVend. You accept it when you tick the box at sign-up.',
        sections: TERMS_SECTIONS,
    },
    {
        id: 'privacy',
        title: 'Privacy Policy',
        standfirst: 'What we collect, why, who sees it, and what you can ask us to do about it. It forms part of the Terms.',
        sections: PRIVACY_SECTIONS,
    },
];

/** One block of a section: paragraph, sub-heading, list, defined terms, or callout. */
const Block = ({ block }) => {
    if (typeof block === 'string') {
        return <p className="mb-4 last:mb-0">{block}</p>;
    }

    if (block.h) {
        return (
            <h4
                className="font-ledger mt-7 mb-3 text-[0.7rem] uppercase tracking-[0.16em]"
                style={{ color: BRAND_COLORS.accentDeep }}
            >
                {block.h}
            </h4>
        );
    }

    if (block.ul) {
        return (
            <ul className="mb-4 last:mb-0 space-y-2 pl-0">
                {block.ul.map((item) => (
                    // Ruled off rather than bulleted: these are obligations, and a
                    // rule reads closer to a clause than a dot does.
                    <li key={item} className="flex items-baseline gap-3">
                        <span
                            aria-hidden="true"
                            className="flex-none block"
                            style={{ width: 10, height: 1, backgroundColor: BRAND_COLORS.accent, transform: 'translateY(-0.35rem)' }}
                        />
                        <span className="flex-1">{item}</span>
                    </li>
                ))}
            </ul>
        );
    }

    if (block.dl) {
        return (
            <dl className="mb-4 last:mb-0">
                {block.dl.map(([term, definition]) => (
                    <div
                        key={term}
                        className="py-3 border-t last:border-b"
                        style={{ borderColor: BRAND_COLORS.lightGray }}
                    >
                        <dt className="text-sm font-medium" style={{ color: BRAND_COLORS.primary }}>{term}</dt>
                        <dd className="mt-1 text-sm" style={{ color: BRAND_COLORS.graphite }}>{definition}</dd>
                    </div>
                ))}
            </dl>
        );
    }

    if (block.note) {
        return (
            <p
                className="my-5 p-5 text-sm border-l-2"
                style={{ backgroundColor: BRAND_COLORS.light, borderColor: BRAND_COLORS.accent, color: BRAND_COLORS.graphite }}
            >
                {block.note}
            </p>
        );
    }

    if (block.warn) {
        return (
            <p className="my-5 p-5 text-sm border-l-2 border-red-500 bg-red-50 text-gray-700">
                {block.warn}
            </p>
        );
    }

    return null;
};

const LegalPage = () => {
    const { hash } = useLocation();

    // The app deep-links to /legal#terms and /legal#privacy from its sign-up
    // consent checkbox and from Help and Support, so those two anchors must
    // keep working. Section anchors are additional, not a replacement.
    useEffect(() => {
        if (!hash) {
            window.scrollTo(0, 0);
            return;
        }
        const element = document.getElementById(hash.substring(1));
        if (element) element.scrollIntoView({ behavior: 'smooth' });
    }, [hash]);

    return (
        <div className="min-h-screen pb-20" style={{ backgroundColor: BRAND_COLORS.light }}>

            <header className="bg-white border-b py-5 px-6 md:px-12" style={{ borderColor: BRAND_COLORS.lightGray }}>
                <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
                    <a href="/" className="flex items-center gap-3 min-w-0">
                        <BrandMark className="w-9 h-9" />
                        <span
                            className="font-light tracking-widest text-base md:text-lg whitespace-nowrap"
                            style={{ color: BRAND_COLORS.primary }}
                        >
                            SKILLVEND <span style={{ color: BRAND_COLORS.accentDeep }}>LEGAL</span>
                        </span>
                    </a>
                    <a
                        href="/"
                        className="font-ledger text-[0.62rem] uppercase tracking-[0.2em] whitespace-nowrap hover:text-black transition-colors"
                        style={{ color: BRAND_COLORS.graphite }}
                    >
                        Back to home
                    </a>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-6 md:px-12 pt-10 md:pt-14">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-14">

                    {/* Contents — generated from the same data the page renders */}
                    <nav aria-label="Contents" className="hidden md:block md:col-span-4 lg:col-span-3">
                        <div className="sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto pr-2">
                            <p
                                className="font-ledger text-[0.6rem] uppercase tracking-[0.2em] mb-5"
                                style={{ color: BRAND_COLORS.graphite }}
                            >
                                Contents
                            </p>
                            {DOCUMENTS.map((doc) => (
                                <div key={doc.id} className="mb-7">
                                    <a
                                        href={'#' + doc.id}
                                        className="block text-sm font-medium mb-3 hover:opacity-70 transition-opacity"
                                        style={{ color: BRAND_COLORS.secondary }}
                                    >
                                        {doc.title}
                                    </a>
                                    <ol className="space-y-2">
                                        {doc.sections.map((section, i) => (
                                            <li key={section.id} className="flex gap-2 leading-snug">
                                                <span
                                                    className="font-ledger text-[0.6rem] flex-none pt-0.5"
                                                    style={{ color: BRAND_COLORS.accentDeep }}
                                                >
                                                    {String(i + 1).padStart(2, '0')}
                                                </span>
                                                <a
                                                    href={'#' + section.id}
                                                    className="text-[0.78rem] font-light hover:text-black transition-colors"
                                                    style={{ color: BRAND_COLORS.graphite }}
                                                >
                                                    {section.title}
                                                </a>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            ))}
                            <a
                                href="#contact"
                                className="block text-sm font-medium hover:opacity-70 transition-opacity"
                                style={{ color: BRAND_COLORS.secondary }}
                            >
                                Contact and grievances
                            </a>
                        </div>
                    </nav>

                    <main className="md:col-span-8 lg:col-span-9 min-w-0">
                        {DOCUMENTS.map((doc) => (
                            <section
                                key={doc.id}
                                id={doc.id}
                                className="anchor-target bg-white border p-7 md:p-12 lg:p-16 mb-10 shadow-sm"
                                style={{ borderColor: BRAND_COLORS.lightGray }}
                            >
                                <h1
                                    className="font-light tracking-wide leading-tight"
                                    style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', color: BRAND_COLORS.primary }}
                                >
                                    {doc.title.toUpperCase()}
                                </h1>
                                <p
                                    className="font-ledger mt-3 text-[0.62rem] uppercase tracking-[0.16em]"
                                    style={{ color: BRAND_COLORS.graphite }}
                                >
                                    Last updated: {LAST_UPDATED}
                                </p>
                                <p className="mt-5 text-base font-light leading-relaxed" style={{ color: BRAND_COLORS.graphite }}>
                                    {doc.standfirst}
                                </p>
                                <div className="mt-8 h-px w-20" style={{ backgroundColor: BRAND_COLORS.accent }} />

                                <div className="mt-10 font-light leading-[1.8] text-gray-800">
                                    {doc.sections.map((section, i) => (
                                        <section key={section.id} id={section.id} className="anchor-target mb-12 last:mb-0">
                                            <h3 className="flex items-baseline gap-3 mb-4">
                                                <span
                                                    className="font-ledger text-[0.72rem] flex-none"
                                                    style={{ color: BRAND_COLORS.accentDeep }}
                                                >
                                                    {String(i + 1).padStart(2, '0')}
                                                </span>
                                                <span
                                                    className="text-lg md:text-xl font-medium leading-snug"
                                                    style={{ color: BRAND_COLORS.primary }}
                                                >
                                                    {section.title}
                                                </span>
                                            </h3>
                                            <div className="pl-0 md:pl-8">
                                                {section.body.map((block, j) => (
                                                    <Block key={j} block={block} />
                                                ))}
                                            </div>
                                        </section>
                                    ))}
                                </div>
                            </section>
                        ))}

                        {/* Contact — one block serving both documents */}
                        <section
                            id="contact"
                            className="anchor-target bg-white border p-7 md:p-12 lg:p-16 shadow-sm"
                            style={{ borderColor: BRAND_COLORS.lightGray }}
                        >
                            <h2
                                className="font-light tracking-wide"
                                style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', color: BRAND_COLORS.primary }}
                            >
                                CONTACT AND GRIEVANCES
                            </h2>
                            <div className="mt-6 h-px w-20" style={{ backgroundColor: BRAND_COLORS.accent }} />

                            <p className="mt-8 font-light leading-relaxed text-gray-800">
                                The quickest route is Help and Support in the app, which reaches the same team and
                                arrives with the context of your account already attached. You can also write to us.
                            </p>

                            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-px" style={{ backgroundColor: BRAND_COLORS.lightGray }}>
                                <div className="bg-white p-6">
                                    <p className="font-ledger text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.graphite }}>
                                        General enquiries
                                    </p>
                                    <a
                                        href={'mailto:' + COMPANY.email}
                                        className="block mt-3 text-sm font-light hover:underline"
                                        style={{ color: BRAND_COLORS.secondary }}
                                    >
                                        {COMPANY.email}
                                    </a>
                                </div>
                                <div className="bg-white p-6">
                                    <p className="font-ledger text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.graphite }}>
                                        Grievance Officer
                                    </p>
                                    {GRIEVANCE_OFFICER.name ? (
                                        <p className="mt-3 text-sm font-light" style={{ color: BRAND_COLORS.primary }}>
                                            {GRIEVANCE_OFFICER.name}
                                        </p>
                                    ) : null}
                                    <a
                                        href={'mailto:' + GRIEVANCE_OFFICER.email}
                                        className="block mt-1 text-sm font-light hover:underline"
                                        style={{ color: BRAND_COLORS.secondary }}
                                    >
                                        {GRIEVANCE_OFFICER.email}
                                    </a>
                                    <p className="mt-2 text-xs font-light" style={{ color: BRAND_COLORS.graphite }}>
                                        Acknowledged within 24 hours, resolved within 15 days.
                                    </p>
                                </div>
                            </div>

                            <div className="mt-px bg-white p-6 border-t" style={{ borderColor: BRAND_COLORS.lightGray }}>
                                <p className="font-ledger text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.graphite }}>
                                    Registered office
                                </p>
                                <p className="mt-3 text-sm font-light leading-relaxed" style={{ color: BRAND_COLORS.primary }}>
                                    {COMPANY.name}
                                    {COMPANY.address.map((line) => (
                                        <span key={line} className="block">{line}</span>
                                    ))}
                                </p>
                            </div>
                        </section>
                    </main>
                </div>
            </div>
        </div>
    );
};

export default LegalPage;
